import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Banknote, Smartphone, Wallet, ArrowDownRight, ArrowUpRight } from 'lucide-react';
import api from '../../../services/api';
import { SituationCaisse, SituationCanal } from '../../../types';

interface Props {
  dateRange: { start_date?: string; end_date?: string };
}

const fmtGNF = (n: number) =>
  new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(n) + ' GNF';

const fmtShort = (n: number) => {
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)} Md`;
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(0)} K`;
  return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(n);
};

const fmtDate = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const MODE_LABELS: Record<string, string> = {
  especes: 'Espèces',
  carte: 'Carte bancaire',
  cheque: 'Chèque',
  virement: 'Virement',
  mobile: 'Mobile Money',
  'Non précisé': 'Non précisé',
};

/* ── Carte d'un canal (espèces ou mobile money) ─────────────── */
const CanalCard: React.FC<{
  titre: string;
  icone: React.ReactNode;
  accent: string;
  canal: SituationCanal;
}> = ({ titre, icone, accent, canal }) => (
  <div
    className="bg-white rounded-2xl overflow-hidden"
    style={{ border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
  >
    <div className="h-[3px] w-full" style={{ background: accent }} />
    <div className="p-5">
      <div className="flex items-center justify-between mb-4">
        <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-400">{titre}</p>
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background: `${accent}1a`, color: accent }}
        >
          {icone}
        </div>
      </div>

      <p className="text-xs text-gray-400">Net de la période (encaissé − décaissé)</p>
      <div className="flex items-baseline gap-1.5 mt-1">
        <span
          className="text-[2rem] font-black leading-none tabular-nums"
          style={{ color: canal.net_periode >= 0 ? '#0f172a' : '#ef4444' }}
        >
          {fmtShort(canal.net_periode)}
        </span>
        <span className="text-sm text-gray-400 font-medium">GNF</span>
      </div>

      <dl className="mt-4 space-y-2 text-sm">
        <div className="flex justify-between">
          <dt className="text-gray-500 flex items-center gap-1.5">
            <ArrowUpRight className="w-3.5 h-3.5 text-emerald-500" /> Encaissements
          </dt>
          <dd className="font-semibold text-gray-800 tabular-nums">{fmtGNF(canal.encaissements)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-gray-500 flex items-center gap-1.5">
            <ArrowDownRight className="w-3.5 h-3.5 text-red-500" /> Dépenses journalières
          </dt>
          <dd className="font-semibold text-gray-800 tabular-nums">
            {fmtGNF(canal.decaissements_depenses)}
          </dd>
        </div>
        {canal.decaissements_autres > 0 && (
          <div className="flex justify-between">
            <dt className="text-gray-500 pl-5">Autres décaissements</dt>
            <dd className="font-medium text-gray-600 tabular-nums">
              {fmtGNF(canal.decaissements_autres)}
            </dd>
          </div>
        )}
        <div className="flex justify-between pt-2 border-t border-gray-100">
          <dt className="text-gray-500">
            Solde au {canal.date_dernier_folio ? fmtDate(canal.date_dernier_folio) : '—'}
          </dt>
          <dd className="font-bold text-gray-900 tabular-nums">{fmtGNF(canal.solde_dernier_folio)}</dd>
        </div>
      </dl>
    </div>
  </div>
);

/* ── Vue principale ─────────────────────────────────────────── */
export default function CaisseMobileMoney({ dateRange }: Props) {
  const [data, setData] = useState<SituationCaisse | null>(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<SituationCaisse>('/dashboard/analytics/situation-caisse/', {
        params: {
          ...(dateRange.start_date ? { start_date: dateRange.start_date } : {}),
          ...(dateRange.end_date ? { end_date: dateRange.end_date } : {}),
        },
      })
      .then((r) => { if (!cancelled) { setData(r.data); setErreur(null); } })
      .catch(() => { if (!cancelled) setErreur('Impossible de charger la situation de caisse.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateRange.start_date, dateRange.end_date]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="bg-white rounded-2xl p-6 animate-pulse h-48"
            style={{ border: '1px solid rgba(0,0,0,0.07)' }} />
        ))}
      </div>
    );
  }

  if (erreur || !data) {
    return <div className="bg-white rounded-2xl p-8 text-center text-gray-500">{erreur}</div>;
  }

  const graphe = data.detail_jours.map((jour) => ({
    date: fmtDate(jour.date).slice(0, 5),
    'Espèces (net)': jour.especes.encaissements - jour.especes.decaissements,
    'Mobile Money (net)': jour.mobile_money.encaissements - jour.mobile_money.decaissements,
  }));

  return (
    <div className="space-y-5">
      {/* Bandeau de synthèse */}
      <div
        className="rounded-2xl px-6 py-5 text-white"
        style={{ background: 'linear-gradient(135deg, #0f766e 0%, #0369a1 100%)' }}
      >
        <p className="text-teal-100 text-xs uppercase tracking-[0.08em] font-bold">
          Disponible après paiement des dépenses journalières
        </p>
        <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2 mt-2">
          <div>
            <span className="text-3xl font-black tabular-nums">{fmtShort(data.total.net_periode)}</span>
            <span className="ml-1.5 text-sm text-teal-100">GNF nets sur la période</span>
          </div>
          <div className="text-sm text-teal-100">
            Soldes de fin :{' '}
            <span className="font-semibold text-white">{fmtGNF(data.total.disponible)}</span>
            {' '}(espèces + Mobile Money)
          </div>
        </div>
        <p className="text-teal-100/80 text-xs mt-2">
          Période du {fmtDate(data.periode.date_debut)} au {fmtDate(data.periode.date_fin)}
          {!data.periode.personnalisee && ' (mois en cours)'}
        </p>
      </div>

      {/* Les deux canaux + dépenses hors caisse */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <CanalCard
          titre="Caisse espèces"
          icone={<Banknote className="w-[18px] h-[18px]" />}
          accent="#0d9488"
          canal={data.especes}
        />
        <CanalCard
          titre="Mobile Money"
          icone={<Smartphone className="w-[18px] h-[18px]" />}
          accent="#f59e0b"
          canal={data.mobile_money}
        />

        {/* Dépenses réglées hors caisse journalière */}
        <div
          className="bg-white rounded-2xl overflow-hidden"
          style={{ border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
        >
          <div className="h-[3px] w-full" style={{ background: '#6366f1' }} />
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-400">
                Dépenses mensuelles
              </p>
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ background: '#6366f11a', color: '#6366f1' }}
              >
                <Wallet className="w-[18px] h-[18px]" />
              </div>
            </div>

            <p className="text-xs text-gray-400">Réglées hors caisse journalière</p>
            <div className="flex items-baseline gap-1.5 mt-1">
              <span className="text-[2rem] font-black leading-none tabular-nums text-gray-900">
                {fmtShort(data.hors_caisse.total)}
              </span>
              <span className="text-sm text-gray-400 font-medium">GNF</span>
            </div>
            <p className="mt-1 text-xs text-gray-400">
              {data.hors_caisse.nombre} dépense{data.hors_caisse.nombre > 1 ? 's' : ''} (salaires,
              factures fournisseurs)
            </p>

            {data.hors_caisse.par_categorie.length > 0 ? (
              <ul className="mt-4 space-y-1.5 text-sm">
                {data.hors_caisse.par_categorie.slice(0, 4).map((ligne) => (
                  <li key={ligne.categorie} className="flex justify-between gap-3">
                    <span className="text-gray-500 truncate">{ligne.categorie}</span>
                    <span className="font-medium text-gray-800 tabular-nums whitespace-nowrap">
                      {fmtShort(ligne.montant)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-4 text-sm text-gray-400">Aucune dépense mensuelle sur la période.</p>
            )}

            {data.hors_caisse.par_mode.length > 0 && (
              <p className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                Réglées par{' '}
                {data.hors_caisse.par_mode
                  .map((m) => `${MODE_LABELS[m.mode] || m.mode} (${fmtShort(m.montant)})`)
                  .join(', ')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Évolution jour par jour */}
      <div
        className="bg-white rounded-2xl p-6"
        style={{ border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
      >
        <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-400 mb-4">
          Flux nets par jour
        </p>
        {graphe.length === 0 ? (
          <p className="text-center text-gray-400 py-8">Aucun mouvement sur la période.</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={graphe}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#94a3b8' }} />
              <YAxis tickFormatter={fmtShort} tick={{ fontSize: 12, fill: '#94a3b8' }} />
              <Tooltip formatter={(v: number) => fmtGNF(v)} />
              <Legend />
              <Bar dataKey="Espèces (net)" fill="#0d9488" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Mobile Money (net)" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Détail jour par jour */}
      {data.detail_jours.length > 0 && (
        <div
          className="bg-white rounded-2xl overflow-hidden"
          style={{ border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-400 px-6 pt-5 pb-3">
            Détail par journée
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr className="text-xs font-medium text-gray-500 uppercase">
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-right">Espèces encaissées</th>
                  <th className="px-4 py-2 text-right">Espèces décaissées</th>
                  <th className="px-4 py-2 text-right">Solde espèces</th>
                  <th className="px-4 py-2 text-right">Mobile encaissé</th>
                  <th className="px-4 py-2 text-right">Mobile décaissé</th>
                  <th className="px-4 py-2 text-right">Solde mobile</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.detail_jours.map((jour) => (
                  <tr key={jour.date} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm text-gray-700">{fmtDate(jour.date)}</td>
                    <td className="px-4 py-2 text-sm text-right tabular-nums text-emerald-600">
                      {fmtShort(jour.especes.encaissements)}
                    </td>
                    <td className="px-4 py-2 text-sm text-right tabular-nums text-red-600">
                      {fmtShort(jour.especes.decaissements)}
                    </td>
                    <td className="px-4 py-2 text-sm text-right tabular-nums font-semibold text-gray-900">
                      {fmtShort(jour.especes.solde)}
                    </td>
                    <td className="px-4 py-2 text-sm text-right tabular-nums text-emerald-600">
                      {fmtShort(jour.mobile_money.encaissements)}
                    </td>
                    <td className="px-4 py-2 text-sm text-right tabular-nums text-red-600">
                      {fmtShort(jour.mobile_money.decaissements)}
                    </td>
                    <td className="px-4 py-2 text-sm text-right tabular-nums font-semibold text-gray-900">
                      {fmtShort(jour.mobile_money.solde)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
